import { isExpectedInstallment, isRecordedActivity } from "./bank-posting";
import type { BankCredit, CardPaymentTimingAdjustment, FinancialSnapshot, Movement } from "./types";
import { creditForCut } from "./bank-credits";
import { paymentTimingForCut } from "./card-payment-timing";
import { pendingBreakdown } from "./pending";

export interface CardCutState {
  period: string;
  start: string;
  end: string;
  movementCount: number;
  expectedCount?: number;
  expectedCents?: number;
  totalCents: number;
  reimbursementCents: number;
  pendingReimbursementCents: number;
  coveredCents: number;
  pendingCents: number;
  ownPendingCents: number;
  bankCreditCents?: number;
  creditRemainderCents?: number;
  paymentTimingCents?: number;
  netCents?: number;
  closed: boolean;
  paid: boolean;
}

export interface CardPaymentRequirement {
  coveredCents: number;
  pendingCents: number;
  pendingReimbursementCents: number;
  ownPendingCents: number;
  inheritedCents: number;
  exactPaymentCents: number;
}

function documentSlug(value: string): string {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

/** El gasto y el pago consultan exactamente el mismo documento de cierre. */
export function cardPaymentEventId(card: string, period: string): string {
  return `payment_${documentSlug(card)}_${documentSlug(period)}`;
}

/**
 * Fusiona la consulta del corte con la fotografía transaccional. Esto incluye
 * un gasto atrasado que haya entrado mientras la pantalla de pago estaba abierta.
 */
export function cardPaymentMovementIds(
  queried: Array<Pick<Movement, "id" | "kind" | "paymentMethod" | "cardPeriod">>,
  snapshotMovements: Array<Pick<Movement, "id" | "kind" | "paymentMethod" | "cardPeriod">>,
  card: string,
  period: string,
): string[] {
  return [...new Set([...queried, ...snapshotMovements]
    .filter((movement) => movement.kind === "expense" && movement.paymentMethod === card && movement.cardPeriod === period)
    .map((movement) => movement.id))];
}

/** Calcula cuánto puede salir de este corte sin tocar la reserva de otro periodo. */
export function cardPaymentRequirement(movements: Movement[], inheritedCents = 0, creditRemainderCents = 0, paymentTimingCents = 0): CardPaymentRequirement {
  const breakdowns = movements.map(pendingBreakdown);
  const coveredCents = breakdowns.reduce((sum, breakdown) => sum + breakdown.coveredCents, 0);
  const pendingCents = breakdowns.reduce((sum, breakdown) => sum + breakdown.movementPendingCents, 0);
  const pendingReimbursementCents = breakdowns.reduce((sum, breakdown) => sum + breakdown.reimbursementPendingCents, 0);
  const inherited = Math.max(0, Math.round(inheritedCents));
  return {
    coveredCents,
    pendingCents,
    pendingReimbursementCents,
    ownPendingCents: Math.max(0, pendingCents - pendingReimbursementCents),
    inheritedCents: inherited,
    exactPaymentCents: Math.max(0, coveredCents + pendingReimbursementCents + inherited - creditRemainderCents + paymentTimingCents),
  };
}

/** Hace pagable una reserva migrada aunque no existan sus compras individuales. */
export function includeInheritedCardCut(
  cuts: CardCutState[],
  period: string | undefined,
  amountCents: number | undefined,
  today: string,
): CardCutState[] {
  const inherited = Math.max(0, Math.round(amountCents ?? 0));
  const bounds = periodBounds(String(period ?? ""));
  if (!inherited || !bounds || cuts.some((cut) => cut.period === period)) return cuts;
  return [
    ...cuts,
    {
      period: period as string,
      start: bounds.start,
      end: bounds.end,
      movementCount: 0,
      totalCents: 0,
      reimbursementCents: 0,
      pendingReimbursementCents: 0,
      coveredCents: 0,
      pendingCents: 0,
      ownPendingCents: 0,
      closed: bounds.end < today,
      paid: false,
    },
  ].sort((a, b) => b.end.localeCompare(a.end));
}

function periodBounds(period: string): { start: string; end: string } | null {
  const match = period.match(/^(\d{4}-\d{2}-\d{2})\s+al\s+(\d{4}-\d{2}-\d{2})$/i);
  return match ? { start: match[1], end: match[2] } : null;
}

function paidPeriodFromEvent(event: Record<string, unknown>, card: string): string | null {
  const eventCard = String(event.card ?? event.Tarjeta ?? "");
  if (eventCard !== card) return null;
  const period = event.period ?? event.closedPeriod ?? event["Periodo cerrado"];
  return typeof period === "string" && periodBounds(period) ? period : null;
}

export function cardCutStates(
  movements: Movement[],
  events: Array<Record<string, unknown>>,
  card: string,
  today: string,
  credits: BankCredit[] = [],
  timingAdjustments: CardPaymentTimingAdjustment[] = [],
): CardCutState[] {
  const paidPeriods = new Set(events.map((event) => paidPeriodFromEvent(event, card)).filter((period): period is string => Boolean(period)));
  const grouped = new Map<string, Movement[]>();
  movements
    .filter((movement) => movement.kind === "expense" && movement.paymentMethod === card && periodBounds(String(movement.cardPeriod ?? "")))
    .forEach((movement) => grouped.set(movement.cardPeriod as string, [...(grouped.get(movement.cardPeriod as string) ?? []), movement]));

  return [...grouped.entries()].map(([period, allItems]) => {
    const items = allItems.filter(item => item.cardPaymentStatus === "paid" || item.historicalSettlement || isRecordedActivity(item, today));
    const expected = allItems.filter(item => isExpectedInstallment(item));
    const bounds = periodBounds(period)!;
    const reimbursementCents = items.reduce((sum, movement) => sum + Math.max(0, movement.reimbursementCents ?? 0), 0);
    const credit = creditForCut(credits,card,period);
    const paymentTimingCents = paymentTimingForCut(timingAdjustments, card, period);
    const requirement = cardPaymentRequirement(items,0,credit.remainderCents,paymentTimingCents);
    return {
      period,
      start: bounds.start,
      end: bounds.end,
      movementCount: items.length,
      expectedCount: expected.length,
      expectedCents: expected.reduce((sum, item) => sum + (item.billedAmountCents ?? item.amountCents), 0),
      bankCreditCents: credit.totalCents, creditRemainderCents: credit.remainderCents, paymentTimingCents,
      netCents: items.reduce((sum,item)=>sum+(item.billedAmountCents??item.amountCents),0)-credit.totalCents+paymentTimingCents,
      totalCents: items.reduce((sum, movement) => sum + (movement.billedAmountCents ?? movement.amountCents), 0),
      reimbursementCents,
      pendingReimbursementCents: requirement.pendingReimbursementCents,
      coveredCents: requirement.coveredCents,
      pendingCents: requirement.pendingCents,
      ownPendingCents: requirement.ownPendingCents,
      // El día de corte todavía pertenece al periodo; se habilita el pago al
      // día siguiente para impedir cerrar un estado de cuenta incompleto.
      closed: bounds.end < today,
      paid: paidPeriods.has(period) || allItems.every((movement) => movement.cardPaymentStatus === "paid"),
    };
  }).sort((a, b) => b.end.localeCompare(a.end));
}

export function settleCardPeriodMovements(
  movements: Movement[],
  card: string,
  period: string,
  paymentEventId: string,
  paidAt: string,
): { movements: Movement[]; settled: Movement[]; reimbursementsSettledCents: number } {
  const settled: Movement[] = [];
  let reimbursementsSettledCents = 0;
  const updated = movements.map((movement) => {
    if (movement.kind !== "expense" || movement.paymentMethod !== card || movement.cardPeriod !== period) return movement;
    const reimbursementToCredit = pendingBreakdown(movement).reimbursementPendingCents;
    if (reimbursementToCredit > 0) reimbursementsSettledCents += reimbursementToCredit;
    const billed = movement.billedAmountCents ?? movement.amountCents;
    const next: Movement = {
      ...movement,
      paidNow: true,
      coveredAmountCents: billed - (movement.bankCreditAppliedCents ?? 0),
      pendingAmountCents: 0,
      cardStatus: "Pagado",
      cardPaymentStatus: "paid",
      cardPaidAt: paidAt,
      cardPaymentEventId: paymentEventId,
      reimbursementReceived: reimbursementToCredit > 0 ? true : movement.reimbursementReceived,
      reimbursementSettledByPayment: reimbursementToCredit > 0,
      updatedAt: new Date().toISOString(),
      syncState: "synced",
    };
    settled.push(next);
    return next;
  });
  if (!settled.length) throw new Error("No encontré movimientos para el corte seleccionado.");
  return { movements: updated, settled, reimbursementsSettledCents };
}

export function applyCardPaymentToSnapshot(
  snapshot: FinancialSnapshot,
  card: string,
  amountCents: number,
): FinancialSnapshot {
  const amount = Math.round(amountCents);
  const reserved = snapshot.cardPayments.find((item) => item.name === card)?.amountCents ?? 0;
  const nu = snapshot.accounts[0]?.balanceCents ?? 0;
  if (amount <= 0) throw new Error("El pago debe ser mayor que cero.");
  if (amount > reserved) throw new Error("El pago supera el saldo reservado para esta tarjeta.");
  if (amount > nu) throw new Error("El saldo de Nu no alcanza para registrar este pago.");
  return {
    ...snapshot,
    accounts: snapshot.accounts.map((account, index) => index === 0 ? { ...account, balanceCents: account.balanceCents - amount } : account),
    cardPayments: snapshot.cardPayments.map((item) => item.name === card ? { ...item, amountCents: item.amountCents - amount } : item),
  };
}

export function assertCutConfirmedForPayment(movements: Movement[]): void {
  if (movements.some(movement => isExpectedInstallment(movement))) {
    throw new Error("Este corte tiene parcialidades sin confirmar. Revisa sus cargos bancarios antes de registrar el pago; no se marcarán pagadas automáticamente.");
  }
}
